import {
  createContext,
  useContext,
  FC,
  useState,
  SetStateAction,
  Dispatch,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useToken } from "@hightouch/htui";
import { load, save } from "src/utils/storage";

/** Check if the current platform is Mac */
const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export const NAV_EXPANDED_WIDTH = 218;
export const NAV_COLLAPSED_WIDTH = 56;

type NavWidthType = {
  base: string;
  lg: string;
};

/** Nav open breakpoints. Always closed on mobile. */
export const NAV_OPEN_BREAKPOINT: NavWidthType = {
  base: `${NAV_COLLAPSED_WIDTH}px`,
  lg: `${NAV_EXPANDED_WIDTH}px`,
};

/** Nav closed breakpoints. Always closed on mobile. */
export const NAV_CLOSED_BREAKPOINT: NavWidthType = {
  base: `${NAV_COLLAPSED_WIDTH}px`,
  lg: `${NAV_COLLAPSED_WIDTH}px`,
};

export type NavWidthContextType = {
  /** Whether the nav is open or collapsed. */
  isNavOpen: boolean;
  /** Whether the nav can be opened from its collapsed state. Not possible on mobile. */
  canNavOpen: boolean;
  /** The width of the nav. */
  navWidth: NavWidthType;
  /** Set the isNavOpen state. */
  setIsNavOpen: Dispatch<SetStateAction<boolean>>;
};

const defaultContextValue: NavWidthContextType = {
  isNavOpen: true,
  canNavOpen: true,
  navWidth: NAV_OPEN_BREAKPOINT,
  setIsNavOpen: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
};

export const NavWidthContext =
  createContext<NavWidthContextType>(defaultContextValue);
NavWidthContext.displayName = "NavWidthContext";

export const useNavWidth = () => {
  const context = useContext(NavWidthContext);
  if (context === defaultContextValue) {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  }
  return context;
};

interface NavWidthProviderProps {
  children: ReactNode;
}

/**
 * Storage key for sidebar preference - global per user (not workspace-specific).
 * Note: The actual localStorage key will be prefixed with "hightouch-" by the storage utility.
 * This key is also referenced in index.html for the skeleton loader (with the prefix).
 */
const SIDEBAR_STORAGE_KEY = "sidebar_collapsed";

/**
 * CSS class added to <html> element to indicate collapsed sidebar state.
 * This is set by the inline script in index.html before React loads (to prevent layout shift),
 * and must be kept in sync with React state to avoid CSS specificity issues.
 */
const SIDEBAR_COLLAPSED_CLASS = "ht-sidebar-collapsed";

/**
 * Get the saved sidebar collapsed preference from storage.
 * Returns undefined if no preference is saved.
 */
function getSavedSidebarCollapsed(): boolean | undefined {
  const saved = load(SIDEBAR_STORAGE_KEY);
  if (saved === null || saved === undefined) return undefined;
  return saved === true;
}

/**
 * Save the sidebar collapsed preference to storage.
 */
function saveSidebarCollapsed(collapsed: boolean): void {
  save(SIDEBAR_STORAGE_KEY, collapsed);
}

/**
 * Get the initial sidebar state from localStorage.
 * Returns true (open) if no preference is saved.
 */
function getInitialSidebarState(): boolean {
  const savedCollapsed = getSavedSidebarCollapsed();
  // Default to open (true) if no preference saved
  return savedCollapsed !== true;
}

export const NavWidthProvider: FC<NavWidthProviderProps> = ({ children }) => {
  // Initialize from localStorage immediately to avoid flash
  const [isNavOpen, setIsNavOpenState] = useState(getInitialSidebarState);
  const [canNavOpen, setCanNavOpen] = useState(true);

  // Track previous canNavOpen to detect mobile -> desktop transitions
  const prevCanNavOpen = useRef(canNavOpen);

  // Wrapper around setIsNavOpen that also persists the preference to localStorage
  const setIsNavOpen: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setIsNavOpenState((prev) => {
        const newValue = typeof value === "function" ? value(prev) : value;

        // Persist to localStorage (sidebar_collapsed is the inverse of isNavOpen)
        saveSidebarCollapsed(!newValue);

        return newValue;
      });
    },
    [],
  );

  // useToken renders the breakpoint in px, we need to remove `px` from the value
  const largeBreakpoint = Number(
    useToken("breakpoints", "lg").replace("px", ""),
  );

  const handleResize = useCallback(() => {
    const isMobile = window.innerWidth < largeBreakpoint;
    const wasMobile = !prevCanNavOpen.current;

    if (isMobile) {
      // On mobile, force collapsed state (but don't save to localStorage)
      setIsNavOpenState(false);
      setCanNavOpen(false);
    } else {
      setCanNavOpen(true);
      // Only restore saved preference when transitioning from mobile to desktop
      if (wasMobile) {
        const savedCollapsed = getSavedSidebarCollapsed();
        setIsNavOpenState(savedCollapsed !== true);
      }
    }

    prevCanNavOpen.current = !isMobile;
  }, [largeBreakpoint]);

  useEffect(() => {
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // Sync the HTML class with React state to prevent CSS specificity issues.
  // The class is initially set by index.html inline script before React loads.
  useEffect(() => {
    if (isNavOpen) {
      document.documentElement.classList.remove(SIDEBAR_COLLAPSED_CLASS);
    } else {
      document.documentElement.classList.add(SIDEBAR_COLLAPSED_CLASS);
    }
  }, [isNavOpen]);

  // Expose the resolved nav width as a CSS custom property on <html> so
  // portal-rendered components (e.g. the sidebarInset Drawer) can offset by the
  // sidebar without reading React context — useNavWidth() throws outside this
  // provider, and the library Drawer renders in a body-level portal.
  useEffect(() => {
    const resolvedWidth =
      canNavOpen && isNavOpen ? NAV_EXPANDED_WIDTH : NAV_COLLAPSED_WIDTH;
    document.documentElement.style.setProperty(
      "--ht-nav-width",
      `${resolvedWidth}px`,
    );
  }, [isNavOpen, canNavOpen]);

  // Keyboard shortcut to toggle nav (⌘/ on Mac, Ctrl+/ on Windows)
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Check for ⌘/ (Mac) or Ctrl+/ (Windows)
      const isToggleShortcut =
        event.key === "/" && (isMac ? event.metaKey : event.ctrlKey);

      if (!isToggleShortcut) {
        return;
      }

      // Don't trigger when typing in an input or textarea
      const activeElement = document.activeElement;
      const isTyping =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute("contenteditable") === "true";

      if (isTyping) {
        return;
      }

      event.preventDefault();
      setIsNavOpen((prev) => !prev);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setIsNavOpen]);

  const navWidth = isNavOpen ? NAV_OPEN_BREAKPOINT : NAV_CLOSED_BREAKPOINT;

  const value = useMemo(
    () => ({ navWidth, isNavOpen, setIsNavOpen, canNavOpen }),
    [navWidth, isNavOpen, setIsNavOpen, canNavOpen],
  );

  return (
    <NavWidthContext.Provider value={value}>
      {children}
    </NavWidthContext.Provider>
  );
};

/**
 * Hook to collapse the nav on mount.
 * Useful for pages that need more horizontal space (e.g., canvas/graph views).
 */
export function useCollapsedNav() {
  const { setIsNavOpen } = useNavWidth();

  useEffect(() => {
    setIsNavOpen(false);
  }, [setIsNavOpen]);
}
